import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ComponentFactoryResolver,
  ComponentRef,
  Injector,
  ViewContainerRef,
  EventEmitter, Output, AfterViewInit, AfterViewChecked,
} from '@angular/core';
import { Userstory } from '../../models/userstory';
import { SynchronizationService } from '../../services/synchronization.service';
import { Subscription } from 'rxjs';
import { Issue } from '../../models/issue';
import { HTMLElementPositioningHelper } from '../../util/htmlelement-positioning-helper';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { IssuecardComponent } from '../issuecard/issuecard.component';
import { IssueSynchroHelper } from './issue.synchro.helper';
import { Status } from '../../models/status';
import { IssuecardHelper } from '../issuecard/issuecard.helper';
import { IssueType } from '../../models/enums/issueType';
import { UserstoryService } from '../../services/userstory.service';
import { EventIssuecardOpened } from '../../models/syncEvents/realtimeEvents/eventIssuecardOpened';
import { EventIssueOpened } from '../../models/syncEvents/persistenceEvents/eventIssueOpened';
import { DependencyEditmodeService } from '../../services/dependency-editmode.service';
import { SyncEventType } from '../../models/enums/syncEventType';
import { EventIssueUpdated } from '../../models/syncEvents/persistenceEvents/eventIssueUpdated';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { Person } from '../../models/person';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { Listener, CommunicationService } from '../../services/communication.service';
import {OnlineuserService} from '../../services/onlineuser.service';
import { EventAllIssuesMarkedAsRead } from '../../models/syncEvents/persistenceEvents/eventAllIssuesMarkedAsRead';

@Component({
  selector: 'app-issue',
  templateUrl: './issue.component.html',
  styleUrls: ['./issue.component.scss']
  })
/** This class represents a small issue card in the actionviews. */
export class IssueComponent implements OnInit, OnDestroy, Listener<AwallComponentEvent> {
  @Input() public issue: Issue;
  @Input() public projectKey: string;
  // IssueType of the issue or the issues parent, if issue is a subissue
  @Input() public inheritedIssueType: IssueType;
  @Input() private statuses: Status[];
  @Input() public onIssueClicked: Function;
  @Output() issueCreated = new EventEmitter<Issue>();
  @Output() subIssueCreated = new EventEmitter<Issue>();
  @Output() issueRemoved = new EventEmitter<number>();
  @Output() subIssueRemoved = new EventEmitter<number>();
  @Output() issuecardClosed = new EventEmitter<Issue>();
  @Output() issueModified = new EventEmitter<void>();
  public avatars: string[] = [];
  public componentRef;
  private subscriptions: Subscription[] = [];

  constructor(
    protected communicationService: CommunicationService,
    private dynamicElementService: DynamicElementService,
    private positionHelper: HTMLElementPositioningHelper,
    private issueCardHelper: IssuecardHelper,
    private syncService: SynchronizationService,
    private resolver: ComponentFactoryResolver,
    public dependencyEditmodeService: DependencyEditmodeService,
    public userstoryService: UserstoryService,
    private onlineUserService: OnlineuserService) {
  }

  ngOnInit() {
    const componentId = IssueComponent.getIssueComponentId(this.projectKey, this.issue.id);
    this.communicationService.registerComponent(componentId, this);
    this.initIssueUpdatedSubscription();
    this.initAllIssuesMarkedAsReadSubscription();
  }

  ngOnDestroy(): void {
    const componentId = IssueComponent.getIssueComponentId(this.projectKey, this.issue.id);
    this.communicationService.unregisterComponent(componentId);
    for (const event of this.subscriptions) {
      event.unsubscribe();
    }
  }

  public static getIssueComponentId(projectKey: string, issueId: number): string {
    return IssueComponent.name + ';' + projectKey + ';' + issueId;
  }

  private initIssueUpdatedSubscription(): void {
    this.subscriptions.push(this.syncService.subscribeEvent<Userstory>(SyncEventType.ISSUE_UPDATED).subscribe(
      payload => {
        const event = EventIssueUpdated.fromPayload(payload);
        if (Number(event.issue.id) === Number(this.issue.id)) {
          this.issue.update(event.issue);
          this.issue.updatedAt = new Date().toISOString();
          this.issueModified.emit();
        }
      }
    ));
  }

  private initAllIssuesMarkedAsReadSubscription(): void {
    this.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ALL_ISSUES_MARKED_AS_READ).subscribe(
      payload => {
        const event = EventAllIssuesMarkedAsRead.fromPayload(payload);
        this.issue.lastViewedInAwall = event.timestamp;
      }
    ));
    this.subscriptions.push(this.userstoryService.allIssuesMarkedAsRead$.subscribe(
      timestamp => {
        this.issue.lastViewedInAwall = timestamp;
      }
    ));
  }

  createIssuecardForIssue(event: MouseEvent, issue: Issue, onCreateAssignToSprint = true): void {
    // Dont open a new card if a card for this issue already exists
    if (this.dynamicElementService.containsIssuecardForIssue(issue.id)) {
      return;
    }
    this.syncService.sendPersistenceEventNew(new EventIssueOpened(issue.id, this.onlineUserService.getAvatarUrl()));

    // Create a new card and open it at the correct position
    const card: IssuecardComponent = this.dynamicallyAddIssueCardComponent(issue, '',onCreateAssignToSprint);
    card.avatars = this.avatars;
    const cardElement = card.getElementRef().nativeElement;
    const pos = this.positionHelper.getPositionForNewCard(event, cardElement);
    this.positionHelper.setAbsolutePosition(cardElement, pos.x, pos.y);
    IssueSynchroHelper.sendIssueCardOpenedEvent(this.syncService, card, this.positionHelper,this.onlineUserService);
  }


  // TODO this is confusing, we should try to find another way to do this
  // (problem: click on issue in mpc-board needs to be handled differently
  // depending on the state of DependencyEditMode )
  handleIssueClicked(event: MouseEvent, issue: Issue): void {
    if (this.onIssueClicked) {
      if (this.onIssueClicked(issue, this.projectKey)) {
        this.createIssuecardForIssue(event, issue);
      }
    } else {
      this.createIssuecardForIssue(event, issue);
    }
  }

  public onRemoteCardOpened(event: EventIssuecardOpened) {
    this.userstoryService.setLastViewed(this.projectKey, this.issue.id).subscribe();
    this.issue.lastViewedInAwall = new Date().toISOString();
    IssueSynchroHelper.handleIssueCardOpenedEvent(event, this.dynamicElementService, this.positionHelper,
      () => this.dynamicallyAddIssueCardComponent(this.issue, event.avatar));
  }

  // TODO: onCreateAssignToSprint is a workaround for as long as IssueCard createIssue() can't handle
  // sprint assignment dynamically
  // TODO this is in large parts a duplicate to issuecardhelper.createCard
  dynamicallyAddIssueCardComponent(issue: Issue, avatar='', onCreateAssignToSprint = true): IssuecardComponent {
    const injector = Injector.create({
      providers: [
        { provide: 'issue', useValue: issue },
        { provide: 'projectKey', useValue: this.projectKey },
        { provide: 'statuses', useValue: this.statuses },
        { provide: 'avatar', useValue: avatar},
        {
          provide: 'onCloseCallback', useValue: (issueCard) => {
            if (this.syncService.realtimeProjectValid()){
              this.removeAvatare();
            }else {
              if (this.dynamicElementService.getAllIssueCards().length === 2){
                console.log(this.dynamicElementService.getAllIssueCards()[0].issue.description);
                this.syncService.sendPersistenceEventNew(new EventIssueOpened(this.dynamicElementService.getAllIssueCards()[0].issue.id,
                  this.onlineUserService.getAvatarUrl()));
              }
            }
            this.destroyIssueCard(issueCard);
            if (this.componentRef) { this.componentRef.destroy(); }
            if (issueCard) { issueCard.componentRef.destroy(); }
            this.issuecardClosed.emit(issue);
          }
        },
        {
          provide: 'onNewIssueCreatedCallback', useValue: (createdIssue) => {
            this.issueCreated.emit(createdIssue);
          }
        },
        {
          provide: 'onNewSubIssueCreatedCallback', useValue: (createdSubIssue) => {
            this.subIssueCreated.emit(createdSubIssue);
          }
        },
        {
          provide: 'onIssueRemovedCallback', useValue: (issueId) => {
            this.issueRemoved.emit(issueId);
          }
        },
        {
          provide: 'onSubIssueRemovedCallback', useValue: (subissueId) => {
            this.subIssueRemoved.emit(subissueId);
          }
        },
        {
          provide: 'onIssueModified', useValue: () => {
            this.issue.updatedAt = new Date().toISOString();
            this.issueModified.emit();
          }
        }
      ]
    });

    const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
    const componentRef: ComponentRef<IssuecardComponent> = insertionPoint.createComponent(IssuecardComponent, { injector: injector });
    const card: IssuecardComponent = componentRef.instance;
    card.componentRef = componentRef;
    card.onCreateAssignToSprint = onCreateAssignToSprint;

    this.issueCardHelper.setupIssueCardAsDraggable(card);
    this.issueCardHelper.setupIssueCardAsClickable(card);
    this.dynamicElementService.insert(componentRef);

    // Update last viewed timestamp when card is created/opened
    this.userstoryService.setLastViewed(this.projectKey, issue.id).subscribe();
    issue.lastViewedInAwall = new Date().toISOString();

    return card;
  }

  destroyIssueCard(card: IssuecardComponent) {
    this.issueCardHelper.removeCardFromGlobalReferences(card);
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    switch (event.name) {
      case ComponentEvent.ISSUE_TEAMMEMBER_DROPPED: {
        const person: Person = <Person>event.value['person'];
        const isReporter: boolean = event.value['isReporter'];
        this.assignDroppedTeammember(person, isReporter);
      }
    }
  }

  private assignDroppedTeammember(teammember: Person, assignAsReporter: boolean): void {
    const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(this.issue.id);
    // Set assigne
    if (!assignAsReporter) {
      this.issue.assignee = teammember;
      this.issue.updatedAt = new Date().toISOString();
      if (!card) {
        // If no card is open persist the change
        this.userstoryService.setAssigneeForIssue(this.projectKey, this.issue, teammember);
      } else {
        // If a card is open user decides if he discards or saves the changes when closing the card
        card.issueAssigneeChanged(teammember);
      }
    }
    // Reporter can only be set on an open card. Update the cards issue
    if (assignAsReporter && card) {
      this.issue.reporter = teammember;
      this.issue.updatedAt = new Date().toISOString();
      card.issueReporterChanged(teammember);
    }
  }
  public updateAvatare(avatar: string) {
    if (this.avatars.filter(avatarList => avatarList === avatar).length === 0 && avatar !== '') {
      this.avatars.push(avatar);
    }
  }
  public removeAvatar(avatar: string) {
    this.avatars = this.avatars.filter(avatarList => avatarList !== avatar);
  }
  public removeAvatare() {
    this.avatars = [];
  }

  // ngAfterViewChecked(){
  // const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
  // const componentRef: ComponentRef<IssuecardComponent> = insertionPoint.createComponent(IssuecardComponent, { injector: injector });
  // const card: IssuecardComponent = componentRef.instance;
  // TODO View Issue not model -> das dann gleich no wrap setzen
  // [...document.querySelectorAll('.cardtext')].forEach(c=>console.log(c));
  // loop cards
  //     const nodes = document.querySelectorAll('.cardtext');
  //     const iterator = nodes[Symbol.iterator]();
  //     let step = iterator.next();
  //     while (!step.done) {
  //       console.log(step.value);
  //       step = iterator.next();
  //     }
  // console.log(cards)
  // }
}
